import { getAliasQueryParam, getDeviceQueryParam } from '@/utils/browser';
import { SelectedAgent } from '@/features/ai-agents/shared/models';

export const SHOPIFY_POST_PURCHASE_STG = `https://sdk.loomi-stg.xyz/post-purchase/index.html`;
export const SHOPIFY_POST_PURCHASE_PROD = `https://sdk.loomi-prod.xyz/post-purchase/index.html`;

const BYPASS_PROXY_URLS = [
  `https://storage.googleapis.com/loomi-production-sdk/vsly-shopify-pp-snapshot.html`,
  `https://sdk.loomi-prod.xyz/vsly-shopify-pp-snapshot.html`,
  SHOPIFY_POST_PURCHASE_PROD,
  SHOPIFY_POST_PURCHASE_STG,
];

export enum Env {
  LOCAL = `local`,
  STAGING = `staging`,
  PRODUCTION = `production`,
}

export class Routes {
  private env: string = Env.LOCAL;

  private proxyEnv: string = Env.LOCAL;

  private analytics: string;

  private getPublicSdkDomain() {
    if (Env.PRODUCTION === this.env) {
      return `https://sdk.loomi-prod.xyz/`;
    }
    return `https://sdk.loomi-stg.xyz/`;
  }

  private getEnvPrefix() {
    if (Env.STAGING === this.env) {
      return `/stage/`;
    }
    if (Env.PRODUCTION === this.env) {
      return `/`;
    }
    if (Env.LOCAL === this.env) {
      return `/local/`;
    }

    return `/local/`;
  }

  private getAnalyticsEnvPrefix() {
    if (Env.STAGING === this.analytics) {
      return `/reporting/`;
    }
    return this.getEnvPrefix();
  }

  private getProxyEnvPrefix() {
    if (Env.STAGING === this.proxyEnv) {
      return `/stage/`;
    }
    if (Env.PRODUCTION === this.proxyEnv) {
      return `/`;
    }
    if (Env.LOCAL === this.proxyEnv) {
      return `/local/`;
    }

    return `/local/`;
  }

  withEnv(env: Env, proxyEnv: Env, analytics: any) {
    this.env = env;
    this.analytics = analytics;
    this.proxyEnv = proxyEnv;
  }

  getEnv(): number {
    return (this.env as Env) === Env.PRODUCTION ? 2 : 1;
  }

  getEnvEnum(): Env {
    return this.env as Env;
  }

  logout() {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/auth/logout`;
  }

  userContext() {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/user/context`;
  }

  get ipBlockList() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/ip-block`;
  }

  get downloadPrivateKey() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/rawdata-export/key`;
  }

  get toggleActivityStream() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/rawdata-export/toggle`;
  }

  get getActivityStreamStatus() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/rawdata-export/is-on`;
  }

  get toggleWidgetLazyLoad() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/lazy-load/toggle`;
  }

  get getWidgetLazyLoadStatus() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/lazy-load/is-on`;
  }

  get sessionView() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/session-view`;
  }

  get listBillingTiers() {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/public/billing/tiers/list`;
  }

  get getPricingInfo() {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/billing/info`;
  }

  get getPricingSummary() {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/billing/summary`;
  }

  get upgradeToNewBilling() {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/billing/upgrade/latest`;
  }

  get changePricingTier() {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/billing/tiers/change`;
  }

  get userDetails() {
    return `${this.getEnvPrefix()}lmiapi/editor/web/v1/user/details`;
  }

  updateUserDetails() {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/user/personal-info`;
  }

  setStoreDomain() {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/store/domain`;
  }

  get persistFbToken() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/public/facebook/auth`;
  }

  get persistInstagramToken() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/public/instagram/auth`;
  }

  get encryptAlias() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/encrypt`;
  }

  get integrations() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/integrations`;
  }

  get klavyioDisconnect() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/settings/klavyio/disconnect`;
  }

  get instagramImages() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/instagram/images`;
  }

  get listFbAdAccounts() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/fb/adaccounts`;
  }

  get lisInstagramAccounts() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/instagram/accounts`;
  }

  get listFbCampaigns() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/fb/campaigns`;
  }

  get fbVerify() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/fb/validate`;
  }

  get loadMoreFbCampaigns() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/fb/campaigns/next`;
  }

  get loadMoreFbAdSets() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/fb/adsets/next`;
  }

  get loadMoreFbAds() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/fb/ads/next`;
  }

  get selectAdAccount() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/fb/adaccount`;
  }

  get listFBImages() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/fb/images`;
  }

  get storeSummary() {
    return `${this.getAnalyticsEnvPrefix()}lmiapi/analytics/statistics/v1/store/summary`;
  }

  get priceTestSummary() {
    return `${this.getAnalyticsEnvPrefix()}lmiapi/analytics/statistics/v1/experiences/summary/price-test`;
  }

  get priceTestSummaryDaily() {
    return `${this.getAnalyticsEnvPrefix()}lmiapi/analytics/statistics/v1/experiences/summary/daily/price-test`;
  }

  productsByExperience(): string {
    return `${this.getAnalyticsEnvPrefix()}lmiapi/analytics/statistics/v1/experiences/products`;
  }

  productsByLabel(): string {
    return `${this.getAnalyticsEnvPrefix()}lmiapi/analytics/statistics/v1/store/summary/label/products`;
  }

  get storeDailySummary() {
    return `${this.getAnalyticsEnvPrefix()}lmiapi/analytics/statistics/v2/store/daily/summary`;
  }

  get storeTotals() {
    return `${this.getAnalyticsEnvPrefix()}lmiapi/analytics/statistics/v1/daily/totals`;
  }

  get experienceSummary() {
    return `${this.getAnalyticsEnvPrefix()}lmiapi/analytics/statistics/v1/store/summary/experiences`;
  }

  get experienceSummaryV2() {
    return `${this.getAnalyticsEnvPrefix()}lmiapi/analytics/statistics/v2/store/summary/experiences`;
  }

  get labelSummary() {
    return `${this.getAnalyticsEnvPrefix()}lmiapi/analytics/statistics/v1/store/summary/labels`;
  }

  get productsSummary() {
    return `${this.getAnalyticsEnvPrefix()}lmiapi/analytics/statistics/v1/store/summary/products`;
  }

  dailySummaryResults(): string {
    return `${this.getAnalyticsEnvPrefix()}lmiapi/analytics/statistics/v1/experiences/summary/daily`;
  }

  summaryResults(): string {
    return `${this.getAnalyticsEnvPrefix()}lmiapi/analytics/statistics/v1/experiences/summary`;
  }

  get utmSource(): string {
    return `${this.getAnalyticsEnvPrefix()}lmiapi/analytics/statistics/v1/utm-source/summary`;
  }

  get exportOrders(): string {
    return `${this.getAnalyticsEnvPrefix()}lmiapi/analytics/statistics/v1/experiences/summary/orders`;
  }

  experienceUplift(): string {
    return `${this.getAnalyticsEnvPrefix()}lmiapi/analytics/statistics/v1/uplifts`;
  }

  get globalControlGroup() {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/global-control-group`;
  }

  get experiencesNames() {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/experience/names`;
  }

  get priceTestPrices() {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/experience/price-test-prices`;
  }

  get isThemeTestPublishAllowed() {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/experience/is-theme-publish-allowed`;
  }

  get saveExperienceSessionsType() {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/sessions-type`;
  }

  exportStats(expId: string, version = ``): string {
    return `${this.getEnvPrefix()}lmiapi/v1/experience/${expId}/stats?v=${version}`;
  }

  quickPreview(website: string, isAiMode = false): string {
    if (BYPASS_PROXY_URLS.includes(website)) {
      return website;
    }
    const baseUrl = `${this.getProxyEnvPrefix()}lmiproxy/${encodeURIComponent(
      btoa(unescape(encodeURIComponent(website))),
    )}`;

    let urlWithParams = `${baseUrl}?init-req=true&vslyalias=${getAliasQueryParam()}`;
    const device = getDeviceQueryParam();
    if (device && website.includes(`shopify_checkout_extensibility`)) {
      urlWithParams = `${urlWithParams}&vslyDevice=${device}&vslyUser=${getLoggedInUser()}`;
    }
    if (isAiMode) {
      urlWithParams = `${urlWithParams}&vslyAiMode=true`;
    }
    return urlWithParams;
  }

  cleanQuickPreview(website: string): string {
    if (BYPASS_PROXY_URLS.includes(website)) {
      return website;
    }
    return `${this.getProxyEnvPrefix()}lmiproxy/clean/${encodeURIComponent(
      btoa(unescape(encodeURIComponent(website))),
    )}?init-req=true&vslyalias=${getAliasQueryParam()}`;
  }

  demoVideoLegend(): string {
    return `${this.getEnvPrefix()}lmiapi/editor/web/v1/public/video/demo/metadata`;
  }

  upsertExperience(): string {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/experience`;
  }

  get setExperienceGoals(): string {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/goals`;
  }

  get clearCache(): string {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/clear-cache`;
  }

  duplicateExperience(): string {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/duplicate/experience`;
  }

  deleteExperience(id: string): string {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/experience/${id}`;
  }

  findExperience(id: string): string {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/experience/${id}`;
  }

  listExperiences(): string {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/experience/list`;
  }

  hideUplift(): string {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/experience/uplift/hide`;
  }

  publishExperience(): string {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/experience/publish`;
  }

  archiveExperience(id: string): string {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/experience/${id}/archive`;
  }

  unarchiveExperience(id: string): string {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/experience/${id}/unarchive`;
  }

  hideExperience(id: string): string {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/experience/${id}/hide`;
  }

  pauseExperience(id: string): string {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/experience/${id}/pause`;
  }

  get generatePreviewFromExperience(): string {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/experience/preview`;
  }

  get generatePermanentPreviewFromExperience(): string {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/experience/permanent-preview`;
  }

  sharePreview(): string {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/experience/preview-share`;
  }

  listEntireCatalog(): string {
    return `${this.getEnvPrefix()}lmiapi/catalog/web/v1/list`;
  }

  listCatalog(): string {
    return `${this.getEnvPrefix()}lmiapi/catalog/web/v1/list/paginate`;
  }

  get submitCatalogSync(): string {
    return `${this.getEnvPrefix()}lmiapi/catalog/web/v1/catalog-sync/submit`;
  }

  get getCatalogSyncStatus(): string {
    return `${this.getEnvPrefix()}lmiapi/catalog/web/v1/catalog-sync`;
  }

  get awaitCatalogSyncDone(): string {
    return `${this.getEnvPrefix()}lmiapi/catalog/web/v1/catalog-sync/await`;
  }

  listRecommendationProducts(): string {
    return `${this.getEnvPrefix()}lmiapi/catalog/web/v1/recommendation/products/list`;
  }

  catalogSearch() {
    return `${this.getEnvPrefix()}lmiapi/catalog/web/v1/search`;
  }

  recommendProducts(query: string): string {
    return `${this.getPublicSdkDomain()}api/recommendations/web/public/recommend?q=${query}`;
  }

  experienceVersions(): string {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/experience/versions`;
  }

  signup(): string {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/public/signup`;
  }

  subscriptionStatus(): string {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/public/subscription/status`;
  }

  awaitActiveSubscription(): string {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/public/subscription/await-active`;
  }

  listUsers(): string {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/users`;
  }

  apiKeys(): string {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/api-keys`;
  }

  apiKey(id: string): string {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/api-keys/${id}`;
  }

  initShop(): string {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/public/shops/create`;
  }

  createUser(): string {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/accounts/create`;
  }

  deactivateUser(): string {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/users/deactivate`;
  }

  updateUserRole(): string {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/users/role`;
  }

  verifyLogin(): string {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/public/auth/verify`;
  }

  listenForVerification(): string {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/public/auth/verify/listen`;
  }

  resendSignupEmail(): string {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/public/email/resend`;
  }

  login(): string {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/public/auth/login`;
  }

  fb() {
    this.getEnv();
    let envPrefix = `/stage/`;
    if (window?.location?.origin === `https://visually.io/`) {
      envPrefix = ``;
    }
    return `${envPrefix}lmiapi/accounts/web/v1/public/fb/status`;
  }

  myAccounts(): string {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/my/accounts`;
  }

  selectAccount(): string {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/accounts/select`;
  }

  listAllApps(): string {
    return `${this.getEnvPrefix()}lmiapi/apps-catalog/web/v1/apps/list`;
  }

  get overrideStoreStyle(): string {
    return `${this.getEnvPrefix()}lmiapi/apps-catalog/web/v1/styles/override`;
  }

  getChange(): string {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/change`;
  }

  listStockImages(): string {
    return `${this.getEnvPrefix()}lmiapi/media/web/v1/stock/query`;
  }

  get imageAiGen(): string {
    return `${this.getEnvPrefix()}lmiapi/ai-agents/web/vsly/ai/image/gen`;
  }

  trackStockImageDownload(): string {
    return `${this.getEnvPrefix()}lmiapi/media/web/v1/stock/download`;
  }

  listGalleryImages(): string {
    return `${this.getEnvPrefix()}lmiapi/media/web/v1/gallery/list`;
  }

  softDelImage(): string {
    return `${this.getEnvPrefix()}lmiapi/media/web/v1/gallery/del`;
  }

  uploadMediaToGallery(): string {
    return `${this.getEnvPrefix()}lmiapi/media/web/v1/gallery/upload`;
  }

  uploadGenericToGallery(): string {
    return `${this.getEnvPrefix()}lmiapi/media/web/v1/general/upload`;
  }

  uploadVideoToGallery(): string {
    return `${this.getEnvPrefix()}lmiapi/media/web/v1/video/upload`;
  }

  reSubscribe(alias: string): string {
    return `${this.getEnvPrefix()}lmiapi/shopify-integration/web/v1/re-subscribe/${alias}`;
  }

  get uploadPartnerPayoutsCsv(): string {
    return `${this.getEnvPrefix()}lmiapi/shopify-integration/web/v1/partner/payouts`;
  }

  removeSDK(themeId?: string): string {
    if (themeId) {
      return `${this.getEnvPrefix()}lmiapi/shopify-integration/web/v1/clean-sdk?themeId=${themeId}`;
    }
    return `${this.getEnvPrefix()}lmiapi/shopify-integration/web/v1/clean-sdk`;
  }

  injectSDK(themeId?: string): string {
    if (themeId) {
      return `${this.getEnvPrefix()}lmiapi/shopify-integration/web/v1/inject-sdk?themeId=${themeId}`;
    }
    return `${this.getEnvPrefix()}lmiapi/shopify-integration/web/v1/inject-sdk`;
  }

  injectSDKMetaField(flag: boolean): string {
    return `${this.getEnvPrefix()}lmiapi/shopify-integration/web/v1/inject-sdk?embed_redirect=${flag}`;
  }

  getSubscription(alias: string): string {
    return `${this.getEnvPrefix()}lmiapi/shopify-integration/web/v1/subscription?alias=${alias}`;
  }

  listThemes(alias: string): string {
    return `${this.getEnvPrefix()}lmiapi/shopify-integration/web/v1/themes?alias=${alias}`;
  }

  get themesStatus(): string {
    return `${this.getEnvPrefix()}lmiapi/shopify-integration/web/v1/themes-status`;
  }

  get promotionBarContent(): string {
    return `${this.getPublicSdkDomain()}custom/promobar.html`;
  }

  isPostPurchaseEnabled(alias: string): string {
    return `${this.getEnvPrefix()}lmiapi/shopify-integration/web/v1/is-post-purchase-enabled?alias=${alias}`;
  }

  isABlockEnabled(alias: string, themeId: number): string {
    return `${this.getEnvPrefix()}lmiapi/shopify-integration/web/v1/is-app-block-enabled?alias=${alias}${
      themeId > 0 ? `&themeId=${themeId}` : ``
    }`;
  }

  get getPostPurchase(): string {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/current-post-purchase`;
  }

  getAntiFlickerState(): string {
    return `${this.getEnvPrefix()}lmiapi/shopify-integration/web/v1/anti-flicker/get`;
  }

  setAntiFlickerState(): string {
    return `${this.getEnvPrefix()}lmiapi/shopify-integration/web/v1/anti-flicker/set`;
  }

  renderAntiFlickerState(): string {
    return `${this.getEnvPrefix()}lmiapi/shopify-integration/web/v1/anti-flicker/render`;
  }

  reviewsEndpoint(): string {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/settings/reviews`;
  }

  get customMetrics(): string {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/settings/custom-metrics`;
  }

  get delCustomMetric(): string {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/settings/delete-custom-metric`;
  }

  get getTextAlt(): string {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/alternative-text`;
  }

  trackUserEvent(): string {
    return `${this.getEnvPrefix()}lmiapi/editor/web/v1/user/stats/track`;
  }

  trackStoreEvent(): string {
    return `${this.getEnvPrefix()}lmiapi/editor/web/v1/store/stats/track`;
  }

  getUserStats(): string {
    return `${this.getEnvPrefix()}lmiapi/editor/web/v1/user/stats/list`;
  }

  getStoreStats(): string {
    return `${this.getEnvPrefix()}lmiapi/editor/web/v1/store/stats/list`;
  }

  getInspirations(): string {
    return `${this.getEnvPrefix()}lmiapi/editor/web/v1/inspirations/list`;
  }

  getPublicInspirations(): string {
    return `${this.getEnvPrefix()}lmiapi/editor/web/v1/public/inspirations/list`;
  }

  recommendInspirations(): string {
    return `${this.getEnvPrefix()}lmiapi/editor/web/v1/inspirations/recommend`;
  }

  markInspirationAsSeen(): string {
    return `${this.getEnvPrefix()}lmiapi/editor/web/v1/inspirations/markAsSeen`;
  }

  getInspirationStatus(): string {
    return `${this.getEnvPrefix()}lmiapi/editor/web/v1/inspirations/status`;
  }

  contactMe(): string {
    return `${this.getEnvPrefix()}lmiapi/editor/web/v1/public/misc/contactMe`;
  }

  get paymentDue(): string {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/payment`;
  }

  saveStyles(): string {
    return `${this.getEnvPrefix()}lmiapi/apps-catalog/web/v1/styles`;
  }

  deleteStyle(): string {
    return `${this.getEnvPrefix()}lmiapi/apps-catalog/web/v1/styles/delete`;
  }

  autoSaveStyles(): string {
    return `${this.getEnvPrefix()}lmiapi/apps-catalog/web/v1/styles/autosave`;
  }

  listStyles(): string {
    return `${this.getEnvPrefix()}lmiapi/apps-catalog/web/v1/styles/list`;
  }

  listAccounts(): string {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v2/my/accounts`;
  }

  get audiences() {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/audiences`;
  }

  get togglePrimaryAudience() {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/audiences/primary`;
  }

  get audiencesList() {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/audiences/list`;
  }

  get deleteAudiences() {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/audiences/delete`;
  }

  getProductExcludedTags() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/product-exclude-tags`;
  }

  getIpBlocksByStore() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/ip-blocks/store`;
  }

  upsertIpBlockList() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/ip-blocks`;
  }

  getStoreVariantMetafields() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/variant-metafields`;
  }

  get userSettings() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/settings/user`;
  }

  get postPurchaseSettings() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/post-purchase/settings`;
  }

  setStoreVariantMetafields() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/variant-metafields`;
  }

  getStoreGoogleTags() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/store-google-tags`;
  }

  upsertStoreGoogleTags() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/store-google-tags`;
  }

  upsertProductExcludedTags() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/product-exclude-tags`;
  }

  getExperienceInfo(expId: string) {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/experience-info/get/${expId}`;
  }

  updateExperienceInfo() {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/experience-info/update`;
  }

  updateExperienceAllocation(): string {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/allocation`;
  }

  removeLabels() {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/labels/remove`;
  }

  listLabels() {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/labels/list`;
  }

  experiencesLabels() {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/labels/experience`;
  }

  setShopRTL() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/is-shop-rtl`;
  }

  get isShopRTL(): string {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/is-shop-rtl`;
  }

  get getListSlotsStrategies(): string {
    return `${this.getEnvPrefix()}lmiapi/apps-catalog/web/v1/slots-strategy`;
  }

  saveSlotsStrategy(): string {
    return `${this.getEnvPrefix()}lmiapi/apps-catalog/web/v1/slots-strategy`;
  }

  deleteSlotsStrategy(): string {
    return `${this.getEnvPrefix()}lmiapi/apps-catalog/web/v1/slots-strategy/delete`;
  }

  convertToLocalAiUrl(url: string): string {
    const prefix = `${this.getEnvPrefix()}lmiapi/ai-agents/web/`;
    return url.replace(prefix, `/local/ai/`);
  }

  aiPlaygroundStatus(): string {
    return `${this.getEnvPrefix()}lmiapi/ai-agents/web/health`;
  }

  aiPlaygroundListAgents(): string {
    return `${this.getEnvPrefix()}lmiapi/ai-agents/web/agents`;
  }

  aiPlaygroundListTeams(): string {
    return `${this.getEnvPrefix()}lmiapi/ai-agents/web/teams`;
  }

  aiGetSessionCheckpoints(sessionId: string): string {
    return `${this.getEnvPrefix()}lmiapi/ai-agents/web/vsly/sessions/${sessionId}/checkpoints`;
  }

  aiRestoreSessionCheckpoint(
    agent: SelectedAgent,
    newSessionId: string,
  ): string {
    return `${this.getEnvPrefix()}lmiapi/ai-agents/web/vsly/${agent.type}s/${
      agent.id
    }/sessions/${newSessionId}/restore`;
  }

  aiPlaygroundListWorkflows(): string {
    return `${this.getEnvPrefix()}lmiapi/ai-agents/web/workflows`;
  }

  aiPlaygroundDecodeImage(): string {
    return `${this.getEnvPrefix()}lmiapi/ai-agents/web/image/decode`;
  }

  getStoreProductMetafields() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/product-metafields`;
  }

  setStoreProductMetafields() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/product-metafields`;
  }

  getStoreGeneralSettings() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/settings`;
  }

  setStoreGeneralSettings() {
    return `${this.getEnvPrefix()}lmiapi/store-settings/web/v1/settings`;
  }

  aiPlaygroundListAgentSessions(
    type: string,
    agentId: string,
    userId: string,
  ): string {
    return `${this.getEnvPrefix()}lmiapi/ai-agents/web/${type}/${agentId}/sessions?user_id=${userId}`;
  }

  aiGetAgentSession(
    agent: SelectedAgent,
    sessionId: string,
    userId?: string,
  ): string {
    const url = `${this.getEnvPrefix()}lmiapi/ai-agents/web/sessions/${sessionId}?type=${
      agent.type
    }`;
    if (userId) {
      return `${url}&user_id=${userId}`;
    }
    return url;
  }

  aiDuplicateSession(agentId: string, sessionId: string): string {
    return `${this.getEnvPrefix()}lmiapi/ai-agents/web/vsly/agents/${agentId}/sessions/${sessionId}/duplicate`;
  }

  aiRevertSessionToLastSavedState(
    agentId: string,
    sessionId: string,
    lastSavedMessageId: string,
  ): string {
    return `${this.getEnvPrefix()}lmiapi/ai-agents/web/vsly/agents/${agentId}/sessions/${sessionId}/revert/${lastSavedMessageId}`;
  }

  aiExperienceEditorRun(): string {
    return `${this.getEnvPrefix()}lmiapi/ai-agents/web/vsly/custom/copilot`;
  }

  aiAgentRun(agent: SelectedAgent): string {
    return `${this.getEnvPrefix()}lmiapi/ai-agents/web/vsly/${agent.type}s/${
      agent.id
    }/runs`;
  }

  saveAiWidget(): string {
    return `${this.getEnvPrefix()}lmiapi/apps-catalog/web/v1/ai/widgets/save`;
  }

  getSavedWidgets(): string {
    return `${this.getEnvPrefix()}lmiapi/apps-catalog/web/v1/ai/widgets/list`;
  }

  deleteSavedWidgets(): string {
    return `${this.getEnvPrefix()}lmiapi/apps-catalog/web/v1/ai/widgets/delete`;
  }

  getPendingChangesSummary(): string {
    return `${this.getEnvPrefix()}lmiapi/experience/web/v1/experience/pending-changes/summary`;
  }

  get featureFlags() {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/flags`;
  }

  get toggleFeatureFlag() {
    return `${this.getEnvPrefix()}lmiapi/accounts/web/v1/flags`;
  }

  get shopifyFunctions() {
    return `${this.getEnvPrefix()}lmiapi/shopify-integration/web/v1/shopify-functions`;
  }

  get copilotStarters() {
    return `${this.getEnvPrefix()}lmiapi/editor/web/v1/copilot/starters`;
  }
}

export const routes = new Routes();

const getLoggedInUser = () =>
  // @ts-ignore
  encodeURIComponent(window.vsly_loggedin_account?.store?.email || ``);
